'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { HelpCircle, RotateCcw, LogIn } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

import { ADVERSARIES, Adversary } from '@/lib/adversaries';
import GameCard from './GameCard';
import StatsBar from './StatsBar';
import RulesModal from './RulesModal';
import VictoryOverlay from './VictoryOverlay';

const FLIP_BACK_DELAY = 900;
const TOTAL_PAIRS = 8;
const SHUFFLE_DURATION = 1800;

export type ClientCard = {
  index: number;
  state: 'hidden' | 'flipped' | 'matched';
  adversaryId: string;
  image?: string | null;
  name: string;
  apt?: string | null;
  origin: string;
  primaryColor: string;
  accentColor: string;
  animalType: string;
  emoji: string;
  shuffleOffset?: { x: number; y: number; rotate: number };
};

type GameStatus = 'idle' | 'shuffling' | 'playing' | 'complete';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildDeck(): ClientCard[] {
  const pairs: ClientCard[] = [];
  ADVERSARIES.forEach((adv: Adversary) => {
    const base: Omit<ClientCard, 'index'> = {
      state: 'hidden',
      adversaryId: adv.id,
      image: adv.image ?? null,
      name: adv.name,
      apt: adv.apt ?? null,
      origin: adv.origin,
      primaryColor: adv.primaryColor,
      accentColor: adv.accentColor,
      animalType: adv.animalType,
      emoji: adv.emoji,
    };
    pairs.push({ ...base, index: 0 });
    pairs.push({ ...base, index: 0 });
  });
  return shuffle(pairs).map((c, i) => ({ ...c, index: i }));
}

async function saveScoreToSupabase(
  playerName: string,
  timeSeconds: number,
  moves: number
): Promise<void> {
  const res = await fetch('/api/game/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ player_name: playerName, time_seconds: timeSeconds, moves }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    console.error('[saveScore] Rejected by server:', data?.error ?? res.status);
  }
}

export default function GameBoard() {
  const [gameStatus, setGameStatus] = useState<GameStatus>('idle');
  const [cards, setCards] = useState<ClientCard[]>([]);
  const [moves, setMoves] = useState(0);
  const [pairsMatched, setPairsMatched] = useState(0);
  const [timeSeconds, setTimeSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [savedTime, setSavedTime] = useState(0);
  const [savedMoves, setSavedMoves] = useState(0);
  const [flippedIndices, setFlippedIndices] = useState<number[]>([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shufflePhase, setShufflePhase] = useState(0);

  const { user, profile } = useAuth();

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFirstMount = useRef(true);

  const runShuffleAndStart = useCallback((deck: ClientCard[]) => {
    setIsShuffling(true);
    setIsLocked(true);
    setShufflePhase(1);

    const spreadDeck = deck.map((c) => ({
      ...c,
      shuffleOffset: {
        x: (Math.random() - 0.5) * 60,
        y: (Math.random() - 0.5) * 60,
        rotate: (Math.random() - 0.5) * 30,
      },
    }));
    setCards(spreadDeck);

    setTimeout(() => {
      setShufflePhase(2);
      const reshuffled = shuffle(deck).map((c, i) => ({
        ...c,
        index: i,
        shuffleOffset: { x: 0, y: 0, rotate: 0 },
      }));
      setCards(reshuffled);
    }, SHUFFLE_DURATION / 2);

    setTimeout(() => {
      setIsShuffling(false);
      setIsLocked(false);
      setShufflePhase(0);
      setCards((prev) => prev.map((c) => ({ ...c, shuffleOffset: undefined })));
      setGameStatus('playing');
      // Show rules only on first ever visit using localStorage
      const hasSeenRules = typeof window !== 'undefined' && localStorage.getItem('adversary_rules_seen');
      if (!hasSeenRules) {
        setRulesOpen(true);
      }
    }, SHUFFLE_DURATION + 200);
  }, [user]);

  const startGame = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const deck = buildDeck();
    setMoves(0);
    setPairsMatched(0);
    setTimeSeconds(0);
    setTimerRunning(false);
    setSavedTime(0);
    setSavedMoves(0);
    setFlippedIndices([]);
    setGameStatus('shuffling');
    runShuffleAndStart(deck);
  }, [runShuffleAndStart]);

  useEffect(() => {
    if (timerRunning) {
      timerRef.current = setInterval(() => {
        setTimeSeconds((t) => t + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [timerRunning]);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      const deck = buildDeck();
      setCards(deck);
      setGameStatus('shuffling');
      setTimeout(() => {
        runShuffleAndStart(deck);
      }, 300);
    }
  }, [runShuffleAndStart]);

  const handleCardClick = useCallback((index: number) => {
    if (isLocked || isShuffling) return;

    const card = cards[index];
    if (!card || card.state !== 'hidden') return;
    if (flippedIndices.includes(index)) return;
    if (flippedIndices.length >= 2) return;

    if (!timerRunning) setTimerRunning(true);

    setCards((prev) =>
      prev.map((c) => (c.index === index ? { ...c, state: 'flipped' as const } : c))
    );

    const newFlipped = [...flippedIndices, index];

    if (newFlipped.length === 1) {
      setFlippedIndices(newFlipped);
    } else if (newFlipped.length === 2) {
      setIsLocked(true);
      setFlippedIndices([]);
      const [i1, i2] = newFlipped;
      const c1 = cards[i1];
      const c2 = cards[i2];
      const newMoves = moves + 1;
      setMoves(newMoves);

      if (c1.adversaryId === c2.adversaryId) {
        setCards((prev) =>
          prev.map((c) =>
            c.index === i1 || c.index === i2 ? { ...c, state: 'matched' as const } : c
          )
        );
        const newPairs = pairsMatched + 1;
        setPairsMatched(newPairs);
        setIsLocked(false);

        if (newPairs === TOTAL_PAIRS) {
          setTimerRunning(false);
          setSavedTime(timeSeconds);
          setSavedMoves(newMoves);
          // Save score to Supabase if logged in
          if (user && profile) {
            saveScoreToSupabase(
              profile.username,
              timeSeconds,
              newMoves
            );
          }
          setTimeout(() => setGameStatus('complete'), 400);
        }
      } else {
        setTimeout(() => {
          setCards((prev) =>
            prev.map((c) =>
              c.index === i1 || c.index === i2 ? { ...c, state: 'hidden' as const } : c
            )
          );
          setIsLocked(false);
        }, FLIP_BACK_DELAY);
      }
    }
  }, [isLocked, isShuffling, cards, flippedIndices, moves, pairsMatched, timerRunning, timeSeconds, user, profile]);

  const handlePlayAgain = useCallback(() => {
    startGame();
  }, [startGame]);

  // Suppress unused variable warning for shufflePhase
  void shufflePhase;

  return (
    <div className="w-full flex flex-col" style={{ height: 'calc(100vh - 56px - 2rem)' }}>
      {/* Victory overlay */}
      {gameStatus === 'complete' && (
        <VictoryOverlay
          timeSeconds={savedTime}
          moves={savedMoves}
          isLoggedIn={!!user}
          onPlayAgain={handlePlayAgain}
        />
      )}

      {/* Rules modal */}
      <RulesModal isOpen={rulesOpen} onClose={() => {
  setRulesOpen(false);
  if (typeof window !== 'undefined') {
    localStorage.setItem('adversary_rules_seen', '1');
  }
}} />

      {/* Game header */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2 flex-shrink-0">
        <div>
          <h1 className="text-base font-bold text-foreground">Adversary Match</h1>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Identify all 8 CrowdStrike threat actors — as fast as possible
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!user && (
            <Link
              href="/sign-up-login-screen"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            >
              <LogIn size={11} />
              Sign in to save
            </Link>
          )}
          <button
            onClick={() => setRulesOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          >
            <HelpCircle size={11} />
            Rules
          </button>
          <button
            onClick={handlePlayAgain}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted border border-border text-[11px] font-medium text-foreground hover:bg-secondary transition-all duration-150 active:scale-95"
          >
            <RotateCcw size={11} />
            New Game
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="mb-2 flex-shrink-0">
        <StatsBar
          timeSeconds={timeSeconds}
          moves={moves}
          pairsMatched={pairsMatched}
          totalPairs={TOTAL_PAIRS}
          isRunning={timerRunning}
        />
      </div>

      {/* Card grid */}
      {cards.length > 0 && (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2 w-full max-w-2xl" style={{ aspectRatio: '1/1' }}>
            {cards.map((card) => (
              <GameCard
                key={`card-${card.index}`}
                card={card}
                onClick={() => handleCardClick(card.index)}
                disabled={isLocked || isShuffling || card.state === 'matched'}
                shuffleOffset={card.shuffleOffset}
                isShuffling={isShuffling}
              />
            ))}
          </div>
        </div>
      )}

      {/* Nation-state legend */}
      <div className="mt-2 max-w-2xl mx-auto w-full flex-shrink-0 pb-1">
        <p className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Nation-State Groups</p>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: 'BEAR = Russia', cls: 'badge-russia' },
            { label: 'PANDA = China', cls: 'badge-china' },
            { label: 'KITTEN = Iran', cls: 'badge-iran' },
            { label: 'CHOLLIMA = DPRK', cls: 'badge-dprk' },
            { label: 'SPIDER = eCrime', cls: 'badge-ecrime' },
          ].map((item) => (
            <span
              key={`legend-${item.label}`}
              className={`px-1.5 py-0.5 rounded text-[9px] font-semibold border ${item.cls}`}
            >
              {item.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}